import { Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TypographyComponent } from '@purolator/ui-kit-angular';
import type { CouponFieldStatus } from '../../core/api/coupon/models/coupon.model';
import { EstimatesOrchestratorApiService } from '../../core/api/estimates-orchestrator-api.service';
import { AnalyticsService } from '../../core/integrations/analytics';
import { MonerisCheckoutLoaderService } from '../../core/integrations/moneris/moneris-checkout-loader.service';
import { ServiceAbilityApiService } from '../../core/api/service-ability-api.service';
import { ServiceAvailabilityApiService } from '../../core/api/service-availability-api.service';
import { COUPON_VALIDATION_FAILED } from '../../core/models/estimates-orchestrator';
import { TranslatePipe, TranslationService } from '../../core/i18n';
import {
  SHIPMENT_WIZARD_STEPS,
  ShipmentFlowStateService,
} from '../../core/services/shipment-flow-state.service';
import { environment } from '../../../environments/environment';
import { AlertToast } from '../../shared/components/alert-toast/alert-toast';
import { readPageSkeletonPreviewQueryFlag } from '../../shared/utils/e2e-query-flags';
import { DelayedLoadingController } from '../../shared/utils/delayed-loading';
import {
  containsDisallowedEmoji,
  optionalNoEmojiValidator,
} from '../../shared/validators/emoji.validators';
import { redirectIfCompletedShipmentActive } from '../get-labels/utils/redirect-if-completed-shipment-active';
import { mapAddressValidationTokenToServiceAbilityRequest } from '../ship-from-ship-to/utils/map-shipment-addresses-to-service-ability-request';
import { ShipmentStepper } from '../ship-from-ship-to/sections/shipment-stepper/shipment-stepper';
import {
  buildEstimateManagerPackagePayload,
  buildOrchestratorAccessorialFlags,
  findEstimateManagerProduct,
  mapEstimateManagerPricingToEstimatePricing,
} from '../service-step/sections/delivery-service-selection/build-delivery-service-requests';
import { ReviewAdditionalServicesSummary } from './sections/review-additional-services-summary/review-additional-services-summary';
import { ReviewAddressSummary } from './sections/review-address-summary/review-address-summary';
import { ReviewAndPayActions } from './sections/review-and-pay-actions/review-and-pay-actions';
import { ReviewAndPaySkeleton } from './sections/review-and-pay-skeleton/review-and-pay-skeleton';
import { ReviewCancelShipmentDialog } from './sections/review-cancel-shipment-dialog/review-cancel-shipment-dialog';
import { ReviewEditWarningDialog } from './sections/review-edit-warning-dialog/review-edit-warning-dialog';
import { ReviewEstimateDetails } from './sections/review-estimate-details/review-estimate-details';
import { ReviewInductionSummary } from './sections/review-induction-summary/review-induction-summary';
import { ReviewPackagingSummary } from './sections/review-packaging-summary/review-packaging-summary';
import { ReviewPaymentModal } from './sections/review-payment-modal/review-payment-modal';
import { ReviewPaymentNoticeDialog } from './sections/review-payment-notice-dialog/review-payment-notice-dialog';
import { ReviewTerms } from './sections/review-terms/review-terms';
import { ReviewTermsDialog } from './sections/review-terms-dialog/review-terms-dialog';
import { PaymentFlowService } from './services/payment-flow.service';
import { buildSelectedServiceAfterPickupReestimate } from './utils/build-selected-service-after-pickup-reestimate';
import { computeShipmentSummaryViews } from './utils/build-shipment-summary-views';
import {
  buildSelectedServiceAfterCouponDiscount,
  deriveReviewCouponDiscount,
  isReviewCouponDiscountRejected,
} from './utils/derive-review-coupon-discount';
import {
  resolveCreateDenyMessage,
  resolveCreateDenyTitleKey,
  SHIPMENT_FAILED_TITLE_KEY,
} from './utils/extract-create-shipment-field-error';
import {
  mapCreateShipmentRequest,
  normalizeDeliveryDate,
} from './utils/map-create-shipment-request';
import { mapCreateSuccessToCompletedShipment } from './utils/map-create-success-to-completed-shipment';
import { pickEarliestEstimateProduct } from './utils/pick-earliest-estimate-product';
import { shouldRefreshEstimateForPickup } from './utils/should-refresh-estimate-for-pickup';

/**
 * Create-shipment Review & Pay step (step 4).
 * Reviews shipment details, re-estimates when pickup date ≠ ship date (OM-754),
 * applies coupons, and runs shipment creation with hosted payment.
 */

/** Zero delay so a required pickup re-estimate paints the skeleton on the first frame (OM-754). */
const PICKUP_REESTIMATE_SKELETON_DELAY_MS = 0;
@Component({
  selector: 'app-review-and-pay',
  imports: [
    AlertToast,
    ReviewAdditionalServicesSummary,
    ReviewAddressSummary,
    ReviewAndPayActions,
    ReviewAndPaySkeleton,
    ReviewCancelShipmentDialog,
    ReviewEditWarningDialog,
    ReviewEstimateDetails,
    ReviewInductionSummary,
    ReviewPackagingSummary,
    ReviewPaymentModal,
    ReviewPaymentNoticeDialog,
    ReviewTerms,
    ReviewTermsDialog,
    ShipmentStepper,
    TranslatePipe,
    TypographyComponent,
  ],
  templateUrl: './review-and-pay.html',
  styleUrl: './review-and-pay.scss',
})
export class ReviewAndPay implements OnInit {
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);
  private readonly translation = inject(TranslationService);
  private readonly analytics = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly estimateOrchestratorApi = inject(EstimatesOrchestratorApiService);
  private readonly serviceAbilityApi = inject(ServiceAbilityApiService);
  private readonly serviceAvailabilityApi = inject(ServiceAvailabilityApiService);
  private readonly paymentFlow = inject(PaymentFlowService);
  private readonly monerisCheckoutLoader = inject(MonerisCheckoutLoaderService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly activeStepIndex = 3;
  /**
   * True when enter must re-estimate before showing Review totals (pickup date ≠ ship date).
   * Evaluated once from session state so the first paint can be the skeleton (OM-754).
   */
  private readonly needsPickupReestimateOnEnter = shouldRefreshEstimateForPickup(
    this.shipmentFlowState.load(),
  );
  private readonly pageSkeletonPreview = readPageSkeletonPreviewQueryFlag();
  /**
   * True when the page skeleton should paint (OM-711).
   * Seeded synchronously for pickup re-estimate / preview so the first frame is never full content.
   */
  protected readonly showPageSkeleton = signal(
    this.needsPickupReestimateOnEnter || this.pageSkeletonPreview,
  );
  /**
   * Blocking enter-time load. Coupon/discount does not block initial display (OM-715).
   * Starts pending when a pickup-date re-estimate is required so content does not flash first.
   * `?pageSkeletonPreview=1` keeps pending true for E2E/QA skeleton a11y scans only.
   */
  private readonly initialContentPending = signal(
    this.pageSkeletonPreview || this.needsPickupReestimateOnEnter,
  );
  private readonly delayedInitialLoading = new DelayedLoadingController(
    (show) => {
      this.showPageSkeleton.set(show);
    },
    this.needsPickupReestimateOnEnter || this.pageSkeletonPreview
      ? PICKUP_REESTIMATE_SKELETON_DELAY_MS
      : undefined,
  );
  readonly couponControl = new FormControl('', {
    nonNullable: true,
    validators: [optionalNoEmojiValidator],
  });
  readonly termsAccepted = new FormControl(false, { nonNullable: true });
  protected readonly couponStatus = signal<CouponFieldStatus>('idle');
  protected readonly pendingEditHref = signal<string | null>(null);
  protected readonly termsDialogOpen = signal(false);
  protected readonly termsAgreementError = signal(false);
  protected readonly cancelShipmentDialogOpen = signal(false);
  protected readonly denyMessage = signal('');
  protected readonly denyTitleKey = signal(SHIPMENT_FAILED_TITLE_KEY);
  protected readonly denyOpen = signal(false);
  protected readonly timeoutOpen = signal(false);
  protected readonly paymentModalOpen = this.paymentFlow.modalOpen;
  protected readonly paymentProcessing = this.paymentFlow.processing;
  protected readonly checkoutActive = this.paymentFlow.checkoutActive;

  protected readonly termsAcceptedValue = toSignal(this.termsAccepted.valueChanges, {
    initialValue: this.termsAccepted.value,
  });

  protected readonly editWarningOpen = computed(() => this.pendingEditHref() !== null);
  /** True when a Review modal is open so page chrome is inert for keyboard and axe. */
  protected readonly pageInert = computed(
    () =>
      this.editWarningOpen() ||
      this.termsDialogOpen() ||
      this.cancelShipmentDialogOpen() ||
      this.paymentModalOpen() ||
      this.denyOpen() ||
      this.timeoutOpen(),
  );

  private readonly flowState = signal(this.shipmentFlowState.load());
  private baseSelectedService = this.flowState()?.selectedService ?? null;
  /** List-price JWT_est captured before any discount so restores never reuse a response token. */
  private baseEstimateAttestation = this.flowState()?.attestations?.estimate ?? null;
  /** True when a required pickup-date re-estimate failed; blocks Proceed To Payment. */
  private readonly pickupReestimateFailed = signal(false);
  /** Settles when enter-time estimate refresh / coupon auto-apply finishes (tests await this). */
  private enterEstimatesReady: Promise<void> = Promise.resolve();
  private couponRequestSequence = 0;
  private leaveResolver: ((allowed: boolean) => void) | null = null;
  private allowLeaveWithoutPrompt = false;

  constructor() {
    effect(() => {
      if (this.termsAcceptedValue()) {
        this.termsAgreementError.set(false);
      }
    });
    effect(() => {
      this.delayedInitialLoading.setPending(this.initialContentPending());
    });
    this.destroyRef.onDestroy(() => this.delayedInitialLoading.destroy());
  }

  /**
   * Redirects to Get Labels when a completed shipment is active (no leave prompt).
   * Otherwise redirects to Service when the wizard prerequisite step is incomplete or no service is selected.
   * Re-estimates the selected product when pickup date ≠ ship date (OM-754), then auto-applies `?coupon=`.
   * Preloads the Moneris Checkout script so Proceed To Payment does not wait on it (ESN embeds it
   * up front); a preload failure is ignored because checkout retries the load itself.
   */
  ngOnInit(): void {
    if (this.shipmentFlowState.hasActiveCompletedShipment()) {
      this.allowLeaveWithoutPrompt = true;
      redirectIfCompletedShipmentActive(this.shipmentFlowState, this.router);
      return;
    }

    const state = this.shipmentFlowState.load();
    if (!state?.completedSteps.includes(SHIPMENT_WIZARD_STEPS.service) || !state.selectedService) {
      this.allowLeaveWithoutPrompt = true;
      this.router.navigateByUrl('/service').catch(() => undefined);
      return;
    }

    this.monerisCheckoutLoader.loadScript().catch(() => undefined);
    this.enterEstimatesReady = this.initializeReviewEstimates();
  }

  /**
   * Runs the optional pickup-date re-estimate, then applies a URL or re-applied coupon when present.
   */
  private async initializeReviewEstimates(): Promise<void> {
    const stateBeforeRefresh = this.shipmentFlowState.load();
    const couponBeforeRefresh = stateBeforeRefresh?.coupon?.couponCode?.trim() ?? '';
    const neededPickupRefresh = shouldRefreshEstimateForPickup(stateBeforeRefresh);

    const refreshed = await this.refreshEstimateForPickupDateIfNeeded();
    if (!refreshed) {
      return;
    }

    const couponFromUrl = this.readCouponQueryParam();
    if (couponFromUrl) {
      this.couponControl.setValue(couponFromUrl);
      await this.onCouponApplied(couponFromUrl);
      return;
    }

    // Re-apply a previously saved coupon only after a pickup-date re-estimate cleared it.
    if (neededPickupRefresh && couponBeforeRefresh) {
      this.couponControl.setValue(couponBeforeRefresh);
      await this.onCouponApplied(couponBeforeRefresh);
    }
  }

  /**
   * When Schedule a Pickup uses a date other than the priced ship date, re-runs
   * service-ability → service-availability → Estimates Manager for the selected product
   * so Review display and create/CSOP/DB use the pickup-aligned shipment date (OM-754).
   *
   * @returns false when a required refresh failed; true when refresh succeeded or was not needed
   */
  private async refreshEstimateForPickupDateIfNeeded(): Promise<boolean> {
    const state = this.shipmentFlowState.load();
    if (!shouldRefreshEstimateForPickup(state)) {
      return true;
    }

    const pickupDate = state?.pickupDropOff?.selectedDate?.trim() ?? '';
    const selectedService = state?.selectedService;
    const addressValidationToken = state?.attestations?.addressValidation?.trim() ?? '';
    const packageSelectionToken = state?.attestations?.packageSelection?.trim() ?? '';
    const accountLookupToken = state?.attestations?.account?.trim() ?? '';
    const productCode = selectedService?.productCode?.trim() ?? '';
    const productSelectionToken =
      state?.attestations?.productSelectionByProductCode?.[productCode]?.trim() ?? '';

    if (
      !pickupDate ||
      !selectedService ||
      !addressValidationToken ||
      !packageSelectionToken ||
      !accountLookupToken ||
      !productSelectionToken ||
      !state?.package
    ) {
      this.openPickupReestimateFailure();
      return false;
    }

    // Pending is already true from construction when this path runs; keep it asserted.
    this.initialContentPending.set(true);
    try {
      const serviceAbility = await this.serviceAbilityApi.check(
        mapAddressValidationTokenToServiceAbilityRequest(addressValidationToken, {
          shipDate: pickupDate,
        }),
      );
      const serviceAbilityToken = serviceAbility.attestation?.trim() ?? '';
      if (!serviceAbility.serviceable || !serviceAbilityToken) {
        this.openPickupReestimateFailure();
        return false;
      }

      const serviceAvailability = await this.serviceAvailabilityApi.check({
        serviceAbilityToken,
      });
      const serviceAvailabilityToken = serviceAvailability.attestation?.trim() ?? '';
      if (!serviceAvailabilityToken) {
        this.openPickupReestimateFailure();
        return false;
      }

      const accessorialFlags = buildOrchestratorAccessorialFlags({
        signatureRequirement: state.signatureProtection?.requirement,
        declaredValue: state.additionalCoverage?.declaredValue ?? null,
      });
      const orchestrateResponse = await this.estimateOrchestratorApi.orchestrate({
        productSelectionTokens: [productSelectionToken],
        packageSelectionToken,
        serviceAbilityToken,
        serviceAvailabilityToken,
        accountLookupToken,
        ...buildEstimateManagerPackagePayload(state.package),
        ...accessorialFlags,
      });

      const estimateAttestation = orchestrateResponse.attestation?.trim() ?? '';
      const product = pickEarliestEstimateProduct({
        dates: orchestrateResponse.availableDeliveryDates ?? [],
        productCode,
        transportationMode: selectedService.transportationMode,
      });
      if (!estimateAttestation || !product) {
        this.openPickupReestimateFailure();
        return false;
      }

      const refreshedService = buildSelectedServiceAfterPickupReestimate(
        selectedService,
        product,
        estimateAttestation,
      );
      if (!refreshedService) {
        this.openPickupReestimateFailure();
        return false;
      }

      this.shipmentFlowState.saveAttestation('serviceAbility', serviceAbilityToken);
      this.shipmentFlowState.saveAttestation('serviceAvailability', serviceAvailabilityToken);
      this.shipmentFlowState.saveEstimateAttestation(estimateAttestation);
      this.shipmentFlowState.saveSelectedService(refreshedService);
      this.shipmentFlowState.clearCoupon();
      this.baseSelectedService = refreshedService;
      this.baseEstimateAttestation = estimateAttestation;
      this.flowState.set(this.shipmentFlowState.load());
      this.pickupReestimateFailed.set(false);
      return true;
    } catch {
      this.openPickupReestimateFailure();
      return false;
    } finally {
      if (!readPageSkeletonPreviewQueryFlag()) {
        // Bypass min-hold so a fast re-estimate does not leave skeleton timers pending.
        this.delayedInitialLoading.setPending(false, { bypassMinHold: true });
        this.initialContentPending.set(false);
      }
    }
  }

  /**
   * Surfaces a blocking notice when the pickup-date re-estimate cannot complete.
   */
  private openPickupReestimateFailure(): void {
    this.pickupReestimateFailed.set(true);
    this.denyTitleKey.set(SHIPMENT_FAILED_TITLE_KEY);
    this.denyMessage.set(
      this.translation.translate(
        'createShipment.serviceStep.deliveryService.errors.estimateUnavailable',
        { helpdesk: environment.helpdeskPhone },
      ),
    );
    this.denyOpen.set(true);
  }

  protected readonly summaryViews = computed(() =>
    computeShipmentSummaryViews(
      this.flowState(),
      this.translation,
      false,
      environment.homeLocatorBaseUrl,
    ),
  );

  /**
   * Discounts the selected Product × EDD against the prior estimate attestation.
   * The estimate JWT is the trusted source, so only productCode and the expected
   * delivery date are sent to select the result inside it.
   */
  protected async onCouponApplied(code: string): Promise<void> {
    const trimmed = code.trim();
    if (!trimmed) {
      this.couponRequestSequence += 1;
      this.clearCouponState();
      return;
    }

    if (containsDisallowedEmoji(trimmed)) {
      this.couponControl.updateValueAndValidity();
      this.setCouponInvalid();
      return;
    }

    const flow = this.flowState();
    const email = flow?.addresses?.shipFrom.email?.trim() ?? '';
    const estimateToken = flow?.attestations?.estimate?.trim() ?? '';
    const selectedService = flow?.selectedService;
    const productCode = selectedService?.productCode?.trim() ?? '';
    if (!email || !estimateToken || !productCode || !selectedService?.deliveryDate) {
      this.setCouponInvalid();
      return;
    }

    const requestSequence = ++this.couponRequestSequence;
    this.couponStatus.set('loading');
    try {
      const result = await this.estimateOrchestratorApi.discount({
        estimateToken,
        productCode,
        expectedDeliveryDate: normalizeDeliveryDate(selectedService.deliveryDate),
        coupon: { couponCode: trimmed, email },
      });
      if (requestSequence !== this.couponRequestSequence) {
        return;
      }

      const couponFailed =
        result.warnings?.some((warning) => warning.code === COUPON_VALIDATION_FAILED) === true;
      const product = findEstimateManagerProduct({
        dates: result.availableDeliveryDates,
        productCode: selectedService.productCode,
        deliveryDate: selectedService.deliveryDate,
        transportationMode: selectedService.transportationMode,
      });
      const pricing = mapEstimateManagerPricingToEstimatePricing(product?.estimate?.pricing);
      const discountedPricing = mapEstimateManagerPricingToEstimatePricing(
        product?.estimate?.discountedPricing,
      );
      const estimateAttestation = result.attestation?.trim();
      if (
        !product ||
        !pricing ||
        !discountedPricing ||
        !estimateAttestation ||
        isReviewCouponDiscountRejected({
          couponFailed,
          couponStatus: product.coupon?.status,
          pricing,
          discountedPricing,
          estimateAttestation,
        })
      ) {
        this.setCouponInvalid();
        return;
      }

      const refreshedService = buildSelectedServiceAfterCouponDiscount(
        selectedService,
        product,
        pricing,
        discountedPricing,
        estimateAttestation,
        deriveReviewCouponDiscount(product.coupon, pricing, discountedPricing),
      );
      this.shipmentFlowState.saveSelectedService(refreshedService);
      this.shipmentFlowState.saveEstimateAttestation(estimateAttestation);
      this.shipmentFlowState.saveCoupon({ couponCode: trimmed, email });
      this.flowState.set(this.shipmentFlowState.load());
      this.couponStatus.set('success');
    } catch {
      if (requestSequence !== this.couponRequestSequence) {
        return;
      }
      this.setCouponInvalid();
    }
  }

  /** Element that opened the Terms dialog; restored on close for keyboard users. */
  private termsDialogTrigger: HTMLElement | null = null;

  /**
   * Opens the Terms and Conditions modal from the inline Terms link.
   * Stores the opener so focus can return when the dialog closes (audit ID 14).
   */
  protected onTermsLinkClicked(): void {
    this.termsDialogTrigger = document.querySelector(
      '[data-testid="review-terms-link"]',
    ) as HTMLElement | null;
    this.termsDialogOpen.set(true);
  }

  /**
   * Marks the terms checkbox accepted after I Agree in the modal.
   */
  protected onTermsAgreed(): void {
    this.termsAccepted.setValue(true);
    this.termsAgreementError.set(false);
    this.termsDialogOpen.set(false);
    this.restoreTermsDialogFocus();
  }

  /**
   * Closes the Terms modal without accepting (checkbox stays unchecked).
   */
  protected onTermsDisagreed(): void {
    this.termsAccepted.setValue(false);
    this.termsDialogOpen.set(false);
    this.restoreTermsDialogFocus();
  }

  /**
   * Returns keyboard focus to the Terms link after the modal closes.
   * Deferred past modal teardown / focus-trap / close animation so the opener keeps focus.
   */
  private restoreTermsDialogFocus(): void {
    const trigger = this.termsDialogTrigger;
    this.termsDialogTrigger = null;
    if (!trigger) {
      return;
    }
    const focusOpener = (): void => {
      if (document.contains(trigger)) {
        trigger.focus({ preventScroll: true });
      }
    };
    // Double rAF covers layout + modal unmount; trailing timeout covers kit close animation.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        focusOpener();
        setTimeout(focusOpener, 50);
      });
    });
  }

  /**
   * Proceed To Payment: blocks with an error when terms are unchecked (Moneris must not open).
   * Also redirects to Get Labels when a completed shipment is already active (OM-743).
   */
  protected async onProceedToPayment(): Promise<void> {
    if (this.shipmentFlowState.hasActiveCompletedShipment()) {
      this.allowLeaveWithoutPrompt = true;
      await this.router.navigateByUrl('/get-labels', { replaceUrl: true });
      return;
    }

    if (this.pickupReestimateFailed()) {
      this.openPickupReestimateFailure();
      return;
    }

    if (!this.termsAccepted.value) {
      this.termsAgreementError.set(true);
      return;
    }

    this.termsAgreementError.set(false);
    const language = this.translation.locale() === 'fr' ? 'fr' : 'en';
    let request;
    try {
      request = mapCreateShipmentRequest(this.shipmentFlowState.load(), language);
    } catch {
      this.denyTitleKey.set(SHIPMENT_FAILED_TITLE_KEY);
      this.denyMessage.set(
        this.translation.translate('createShipment.reviewAndPay.paymentDeny.generic'),
      );
      this.denyOpen.set(true);
      return;
    }

    const result = await this.paymentFlow.start(this.shipmentFlowState.ensureFlowId(), request);
    if (result.status === 'success') {
      this.shipmentFlowState.saveCompletedShipment(
        mapCreateSuccessToCompletedShipment(result.response),
      );
      this.allowLeaveWithoutPrompt = true;
      await this.router.navigateByUrl('/get-labels', { replaceUrl: true });
      return;
    }
    if (result.status === 'failed') {
      this.denyTitleKey.set(resolveCreateDenyTitleKey(result));
      this.denyMessage.set(
        resolveCreateDenyMessage(result, (key, params) => this.translation.translate(key, params)),
      );
      this.denyOpen.set(true);
      return;
    }
    if (result.status === 'timeout') {
      this.timeoutOpen.set(true);
    }
  }

  /**
   * Closes the Moneris modal and invalidates the active checkout callback generation.
   */
  protected onPaymentModalClosed(): void {
    this.paymentFlow.cancelFromUi();
  }

  /**
   * Dismisses a safe shipment/payment failure notice without clearing Review state.
   */
  protected onDenyDismissed(): void {
    this.denyOpen.set(false);
    this.denyMessage.set('');
    this.denyTitleKey.set(SHIPMENT_FAILED_TITLE_KEY);
  }

  /**
   * Dismisses the hosted-checkout timeout notice.
   */
  protected onTimeoutDismissed(): void {
    this.timeoutOpen.set(false);
  }

  /**
   * Opens the cancel-shipment confirmation dialog.
   */
  protected onCancelShipmentRequested(): void {
    this.cancelShipmentDialogOpen.set(true);
  }

  /**
   * Keeps the customer on Review & Pay when they decline cancellation.
   */
  protected onCancelShipmentDismissed(): void {
    this.cancelShipmentDialogOpen.set(false);
  }

  /**
   * Clears wizard state and returns to Ship From/Ship To after confirmed cancellation.
   */
  protected onCancelShipmentConfirmed(): void {
    this.cancelShipmentDialogOpen.set(false);
    this.shipmentFlowState.clear();
    this.allowLeaveWithoutPrompt = true;
    this.router.navigateByUrl('/').catch(() => undefined);
  }

  /**
   * Opens the leave warning before navigating back to Service.
   */
  protected onBackRequested(): void {
    this.requestLeave('/service');
  }

  /**
   * Opens the Edit warning dialog for the selected review section.
   */
  protected onEditRequested(href: string): void {
    this.requestLeave(href);
  }

  /**
   * Dismisses the leave warning without leaving Review & Pay.
   */
  protected onEditCancelled(): void {
    this.pendingEditHref.set(null);
    this.resolveLeave(false);
  }

  /**
   * Navigates to the pending destination after the customer confirms the warning.
   */
  protected onEditConfirmed(): void {
    const href = this.pendingEditHref();
    this.pendingEditHref.set(null);
    if (!href) {
      return;
    }

    this.analytics.trackEvent({
      event: 'editlink',
      redirectedto: href,
    });

    if (this.leaveResolver) {
      this.allowLeaveWithoutPrompt = true;
      this.resolveLeave(true);
      return;
    }

    this.allowLeaveWithoutPrompt = true;
    this.router.navigateByUrl(href).catch(() => undefined);
  }

  /**
   * Used by the route guard when browser back or in-app navigation leaves Review & Pay.
   */
  confirmLeave(destinationUrl: string): Promise<boolean> {
    if (this.allowLeaveWithoutPrompt) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.leaveResolver = resolve;
      this.requestLeave(destinationUrl);
    });
  }

  /**
   * Opens the shared leave-warning modal for the requested destination URL.
   */
  protected requestLeave(url: string): void {
    this.pendingEditHref.set(url);
  }

  private resolveLeave(allowed: boolean): void {
    this.leaveResolver?.(allowed);
    this.leaveResolver = null;
  }

  /**
   * Restores the pre-coupon selection so removed coupons cannot leave discounted attestation state.
   */
  private clearCouponState(): void {
    this.restoreBaseEstimate();
    this.shipmentFlowState.clearCoupon();
    this.flowState.set(this.shipmentFlowState.load());
    this.couponStatus.set('idle');
  }

  /**
   * Restores the pre-coupon estimate and exposes the coupon field's invalid state.
   */
  private setCouponInvalid(): void {
    this.restoreBaseEstimate();
    this.shipmentFlowState.clearCoupon();
    this.flowState.set(this.shipmentFlowState.load());
    this.couponStatus.set('invalid');
  }

  /**
   * Replaces any discounted selection with the estimate captured before coupon application.
   * Restores `attestations.estimate` as well as the selected service, otherwise create-shipment
   * would send a discounted JWT_est alongside list pricing.
   */
  private restoreBaseEstimate(): void {
    if (this.baseSelectedService) {
      this.shipmentFlowState.saveSelectedService(this.baseSelectedService);
    }
    if (this.baseEstimateAttestation) {
      this.shipmentFlowState.saveEstimateAttestation(this.baseEstimateAttestation);
    }
    this.flowState.set(this.shipmentFlowState.load());
  }

  /**
   * Reads `?coupon=` case-insensitively (ESN parity).
   */
  private readCouponQueryParam(): string | null {
    const params = this.route.snapshot.queryParamMap;
    for (const key of params.keys) {
      if (key.toLowerCase() === 'coupon') {
        const value = params.get(key)?.trim();
        return value ? value : null;
      }
    }
    return null;
  }
}
